import React, { useState, useEffect, useRef, useMemo } from 'react';

import * as Yup from 'yup';

import { Form } from '@rocketseat/unform';

import { resendCodeService } from 'services/user';

import ReactLoading from 'react-loading';

import { useLayout } from 'hooks/layout';
import { useAuth } from 'hooks/auth';

import history from 'services/history';

import VerificationCodeInput from 'components/VerificationCodeInput';
import SquareCheckbox from 'components/NewComponents/SquareCheckbox';

import { Button, Error, FormInput } from '@cloudez/cloudez-design-system';

import { faUserLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { InputBox, Content, Wrapper } from './styles';

const TwoFactorAuthentication: React.FC = () => {
  const theme = useLayout();
  document.title = `Autenticação de dois fatores — ${theme.name}`;

  const { signIn, loginData, devices, signInError } = useAuth();

  const [isTrustedDevice, setIsTrustedDevice] = useState(false);
  const [codeAuthError, setCodeAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState({
    cod1: '',
    cod2: '',
    cod3: '',
    cod4: '',
    cod5: '',
    cod6: '',
  });

  const handleCodeChange = newCode => {
    setCode(newCode);
  };

  const concatCode = useMemo(
    () =>
      `${code.cod1}${code.cod2}${code.cod3}${code.cod4}${code.cod5}${code.cod6}`,
    [code],
  );

  const [switchMode, setSwitchMode] = useState(false);

  const app = devices?.find(device => device === 'app');
  const email = devices?.find(device => device === 'email');

  const resendCodeToEmail = async () => {
    await resendCodeService(loginData);
  };

  const schema = Yup.object().shape({
    code: Yup.string().required(
      'Verifique o código de segurança e tente novamente.',
    ),
  });

  const handleSubmit = data => {
    schema
      .validate(data, { abortEarly: false })
      .then(async result => {
        setLoading(true);
        await signIn(
          loginData.email,
          loginData.password,
          {},
          {
            code: Object.values(result).join(''),
            trust_device: isTrustedDevice,
            device_type:
              signInError === 'Verification code sent in email' || switchMode
                ? 'email'
                : 'app',
          },
        );
        setLoading(false);
        setCodeAuthError('');
      })
      .catch(error => {
        setCodeAuthError(error.message);
        setLoading(false);
      });
  };

  const Ref = useRef(null);

  const [timer, setTimer] = useState('00:00');

  const getTimeRemaining = e => {
    const total = Date.parse(e) - Date.parse(new Date() as any);
    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    return {
      total,
      minutes,
      seconds,
    };
  };

  const startTimer = e => {
    const { total, minutes, seconds } = getTimeRemaining(e);
    if (total >= 0) {
      setTimer(
        `${minutes > 9 ? minutes : `0${minutes}`}:${
          seconds > 9 ? seconds : `0${seconds}`
        }`,
      );
    }
  };

  const clearTimer = e => {
    setTimer('05:00');
    if (Ref.current) clearInterval(Ref.current);
    const id = setInterval(() => {
      startTimer(e);
    }, 1000);
    Ref.current = id;
  };

  const getDeadTime = () => {
    const deadline = new Date();

    deadline.setSeconds(deadline.getSeconds() + 300);
    return deadline;
  };

  useEffect(() => {
    clearTimer(getDeadTime());
  }, []);

  const resendAndReset = () => {
    resendCodeToEmail();
    clearTimer(getDeadTime());
  };

  const switchAndSendCode = () => {
    if (!switchMode) {
      resendCodeToEmail();
      clearTimer(getDeadTime());
    }
    setSwitchMode(!switchMode);
  };

  useEffect(() => {
    const inputs = document.querySelectorAll('input');

    inputs.forEach((input, index) => {
      input.addEventListener('input', (e: any) => {
        if (e.target.value.length > 0) {
          if (index !== inputs.length - 1) {
            inputs[index + 1].focus();
          } else {
            inputs[index].blur();
          }
        }
      });
    });
  }, []);

  useEffect(() => {
    if (devices?.length === 0) {
      history.push('/login');
    }
  }, [devices]);

  return (
    <Wrapper>
      <Content>
        {!theme || theme.loading ? (
          <ReactLoading type="balls" />
        ) : (
          <>
            <img src={theme.logo_primary} alt="logo" />
            <Form style={{ padding: '28px' }} onSubmit={handleSubmit}>
              <h3>Autenticação de dois fatores</h3>
              <FontAwesomeIcon
                style={{
                  color: theme.darkMode ? theme.interface5 : theme.interface7,
                }}
                size="3x"
                icon={faUserLock}
              />
              <span className="sentCode">
                {app
                  ? 'Insira o código de 6 dígitos que foi enviado para o seu aplicativo'
                  : 'Insira o código de 6 dígitos que foi enviado para o seu e-mail'}
              </span>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  marginBottom: '25px',
                  width: '100%',
                }}
              >
                <InputBox>
                  <VerificationCodeInput
                    noSpace
                    onCodeChange={handleCodeChange}
                  />
                  <FormInput
                    name="code"
                    style={{ display: 'none' }}
                    value={concatCode}
                    type="text"
                  />
                </InputBox>
                {signInError === 'Verification code failed' || codeAuthError ? (
                  <Error>
                    Verifique o código de segurança e tente novamente.
                  </Error>
                ) : null}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '15px',
                }}
              >
                <SquareCheckbox
                  checked={isTrustedDevice}
                  value={isTrustedDevice}
                  onChange={e => setIsTrustedDevice(e.target.checked)}
                  name="trustedDevice"
                />
                <span
                  onClick={() => setIsTrustedDevice(!isTrustedDevice)}
                  style={{ fontSize: '14px' }}
                  className="sentCode"
                >
                  Lembrar este dispositivo por 30 dias
                </span>
              </div>
              <Button
                disabled={loading}
                icon
                width="255px"
                height="50px"
                type="submit"
              >
                {loading ? (
                  <FontAwesomeIcon
                    style={{ marginLeft: '0' }}
                    icon={faSpinner}
                    spin
                  />
                ) : (
                  'Acessar painel'
                )}
              </Button>
              {!app ||
                (switchMode && (
                  <>
                    {timer === '00:00' ? (
                      <span
                        className="sentCode"
                        style={{
                          fontSize: '14px',
                          textDecoration: 'underline',
                        }}
                        onClick={resendAndReset}
                      >
                        Reenviar código
                      </span>
                    ) : (
                      <span
                        className="sentCode"
                        style={{
                          fontSize: '14px',
                          textDecoration: 'underline',
                        }}
                      >
                        Reenviar novamente em {timer}
                      </span>
                    )}
                  </>
                ))}

              {app && email && (
                <span
                  onClick={switchAndSendCode}
                  style={{
                    fontSize: '14px',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  }}
                  className="sentCode"
                >
                  {switchMode
                    ? 'Não tenho acesso ao email e quero usar o aplicativo'
                    : 'Não tenho acesso ao aplicativo e quero receber por e-mail'}
                </span>
              )}
              <div>
                <span
                  style={{
                    fontSize: '14px',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  }}
                  className="sentCode"
                  onClick={() =>
                    window.open(
                      theme.slug === 'configr'
                        ? 'https://help.cloudez.io/nao-consigo-entrar/?cfgr=true'
                        : 'https://help.cloudez.io/nao-consigo-entrar/',
                      '_blank',
                    )
                  }
                >
                  {app
                    ? 'Não tem acesso ao aplicativo autenticador? Clique aqui'
                    : 'Não tem acesso ao email? Clique aqui'}
                </span>
              </div>
            </Form>
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default TwoFactorAuthentication;
